import {
  type DiscountDateAvailabilityPayload,
  type GetAvailabilityFromDateSnapshot,
  type SessionPathDefinition,
  emitDiscountDatesFromSessionSnapshot,
  latestDiscountSessionSnapshotByPath,
  sessionDefinitionToPath,
} from "./subscribeDiscountSessionHelpers";
import subscribe from "./subscriptionManager/subscribe";
import type { DataSnapshot, Unsubscribe } from "../dependencies/firebase";

export type { DiscountDateAvailabilityPayload } from "./subscribeDiscountSessionHelpers";
export {
  clearDiscountSessionSnapshotCache,
  getRememberedDiscountSessionDates,
  getSessionSubscribedDefinitions,
  latestDiscountSessionSnapshotByPath,
  rememberDiscountSessionDates,
  replayDiscountSessionSnapshots,
  sessionDefinitionToPath,
} from "./subscribeDiscountSessionHelpers";

/**
 * Subscribes to all dates under one restaurant/session path, then adapts each
 * snapshot to the existing per-date payload contract.
 */
export default function subscribeDiscountSession(
  pathDefinition: Omit<SessionPathDefinition, "onlyOnce">,
  getDatesForRestaurant: () => string[],
  getAvailabilityFromDateSnapshot: GetAvailabilityFromDateSnapshot,
  unsubOne: (path: string) => void,
  addUnsub: (path: string, unsub: Unsubscribe) => void,
  onValue: (payload: DiscountDateAvailabilityPayload) => Promise<void>,
  abortSignal: AbortSignal,
  onlyOnce = false,
): Promise<void> {
  return subscribe<SessionPathDefinition, DataSnapshot>(
    pathDefinition,
    unsubOne,
    addUnsub,
    sessionDefinitionToPath,
    (snapshot, path) => {
      latestDiscountSessionSnapshotByPath.set(path, snapshot);
      return snapshot;
    },
    async ({ restaurantId, session, availability: snapshot }) => {
      if (!snapshot) {
        return;
      }

      await emitDiscountDatesFromSessionSnapshot(
        snapshot,
        restaurantId,
        session,
        getDatesForRestaurant(),
        getAvailabilityFromDateSnapshot,
        onValue,
      );
    },
    abortSignal,
    onlyOnce,
  );
}
